import React, {useMemo} from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {backgroundColor, flexAbsoluteCenter} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {uniq} from "@pg-mono/nodash";

import {getPlannerMultiWorldSideRoomsDescription} from "../../helpers/planner_property/get_planner_multi_world_side_rooms_description";
import {getPlannerPropertyWorldSideName} from "../../helpers/planner_property/get_planner_property_world_side_name";
import {normalizePlannerPropertyRoomTypes} from "../../helpers/planner_property/normalize_planner_property_room_types";
import {IPlannerPropertyRoom} from "../../types/planner_property/IPlannerPropertyRoom";
import {IPlannerPropertyRoomExposureSide} from "../../types/planner_property/IPlannerPropertyRoomExposureSide";
import {IPlannerRoomNames} from "../../types/planner_property/IPlannerRoomNames";
import {PropertyLocationGeneralAttributeBox} from "./PropertyLocationGeneralAttributeBox";

interface IProps {
    propertyRooms: IPlannerPropertyRoom[];
}

const disableExposureForRooms: string[] = [IPlannerRoomNames.Garden, IPlannerRoomNames.Balcony, IPlannerRoomNames.Terrace];

export const PropertyLocationExposure = (props: IProps) => {
    const theme = useTheme();

    const plannerPropertyRooms = useMemo(() => {
        const validPropertyRooms = props.propertyRooms.filter((room) => !disableExposureForRooms.includes(room.name.toLowerCase()));
        const roomsNormalized = normalizePlannerPropertyRoomTypes(validPropertyRooms, [
            IPlannerRoomNames.Kitchen,
            IPlannerRoomNames.DiningRoom,
            IPlannerRoomNames.Bathroom,
            IPlannerRoomNames.Bedroom,
            IPlannerRoomNames.LivingRoom
        ]);

        const wordSides = uniq(
            validPropertyRooms
                ?.filter((room) => room.exposure)
                .map((room) => room.exposure as IPlannerPropertyRoomExposureSide[])
                .flat() || []
        );
        const hasManySides = wordSides.length > 1;

        const description: string = hasManySides
            ? getPlannerMultiWorldSideRoomsDescription(roomsNormalized)
            : generateSingleWordSideDescription(wordSides[0] as IPlannerPropertyRoomExposureSide);

        return {
            hasManySides,
            wordSides,
            description,
            hasNorthSide: wordSides.includes("N") || wordSides.includes("NE") || wordSides.includes("NW"),
            hasEastSide: wordSides.includes("E") || wordSides.includes("NE") || wordSides.includes("SE"),
            hasWestSide: wordSides.includes("W") || wordSides.includes("NW") || wordSides.includes("SW"),
            hasSouthSide: wordSides.includes("S") || wordSides.includes("SE") || wordSides.includes("SW")
        };
    }, [props.propertyRooms]);

    if (!plannerPropertyRooms.hasNorthSide && !plannerPropertyRooms.hasEastSide && !plannerPropertyRooms.hasWestSide && !plannerPropertyRooms.hasSouthSide) {
        return null;
    }

    return (
        <PropertyLocationGeneralAttributeBox
            graphic={
                <div css={propertySidesWrapperStyle}>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasNorthSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Północ
                        </Text>
                    </div>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasEastSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Wschód
                        </Text>
                    </div>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasWestSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Zachód
                        </Text>
                    </div>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasSouthSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Południe
                        </Text>
                    </div>
                </div>
            }
            title={plannerPropertyRooms.hasManySides ? "Mieszkanie dwustronne" : "Mieszkanie jednostronne"}
            description={plannerPropertyRooms.description}
        />
    );
};

const generateSingleWordSideDescription = (wordSide: IPlannerPropertyRoomExposureSide) => {
    return `Wszystkie okna w wybranym przez Ciebie mieszkaniu wychodzą na ${getPlannerPropertyWorldSideName(wordSide)}.`;
};

const propertySidesWrapperStyle = (theme: Theme) => css`
    overflow: hidden;
    display: grid;
    grid-template-columns: repeat(2, 2fr);
    background-color: ${theme.colors.secondary};
    border: 2px solid ${theme.colors.secondary};
    border-radius: 50%;
    transform: rotate(45deg);
    gap: 2px;
`;

const PROPERTY_BOX_SIZE_REM = 5;

const propertySideStyle = (theme: Theme) => css`
    width: ${PROPERTY_BOX_SIZE_REM}rem;
    height: ${PROPERTY_BOX_SIZE_REM}rem;
    position: relative;
    ${flexAbsoluteCenter};
    background-color: ${theme.colors.gray[200]};

    &:nth-of-type(1) > span,
    &:nth-of-type(4) > span {
        text-align: center;
        width: 100%;
        transform: rotate(-45deg);
    }

    &:nth-of-type(2) > span {
        left: 0;
        bottom: 0;
        transform-origin: center left;
        transform: translate(0, 50%) rotate(-45deg);
        text-align: right;
    }

    &:nth-of-type(3) > span {
        right: 0;
        top: 0;
        transform-origin: center right;
        transform: translate(0, -50%) rotate(-45deg);
    }
`;

const propertySideTextStyle = css`
    display: block;
    position: absolute;
    width: ${PROPERTY_BOX_SIZE_REM - 0.5}rem;
`;
import React, {useMemo} from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {backgroundColor, flexAbsoluteCenter} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {uniq} from "@pg-mono/nodash";

import {getPlannerMultiWorldSideRoomsDescription} from "../../helpers/planner_property/get_planner_multi_world_side_rooms_description";
import {getPlannerPropertyWorldSideName} from "../../helpers/planner_property/get_planner_property_world_side_name";
import {normalizePlannerPropertyRoomTypes} from "../../helpers/planner_property/normalize_planner_property_room_types";
import {IPlannerPropertyRoom} from "../../types/planner_property/IPlannerPropertyRoom";
import {IPlannerPropertyRoomExposureSide} from "../../types/planner_property/IPlannerPropertyRoomExposureSide";
import {IPlannerRoomNames} from "../../types/planner_property/IPlannerRoomNames";
import {PropertyLocationGeneralAttributeBox} from "./PropertyLocationGeneralAttributeBox";

interface IProps {
    propertyRooms: IPlannerPropertyRoom[];
}

const disableExposureForRooms: string[] = [IPlannerRoomNames.Garden, IPlannerRoomNames.Balcony, IPlannerRoomNames.Terrace];

export const PropertyLocationExposure = (props: IProps) => {
    const theme = useTheme();

    const plannerPropertyRooms = useMemo(() => {
        const validPropertyRooms = props.propertyRooms.filter((room) => !disableExposureForRooms.includes(room.name.toLowerCase()));
        const roomsNormalized = normalizePlannerPropertyRoomTypes(validPropertyRooms, [
            IPlannerRoomNames.Kitchen,
            IPlannerRoomNames.DiningRoom,
            IPlannerRoomNames.Bathroom,
            IPlannerRoomNames.Bedroom,
            IPlannerRoomNames.LivingRoom
        ]);

        const wordSides = uniq(
            validPropertyRooms
                ?.filter((room) => room.exposure)
                .map((room) => room.exposure as IPlannerPropertyRoomExposureSide[])
                .flat() || []
        );
        const hasManySides = wordSides.length > 1;

        const description: string = hasManySides
            ? getPlannerMultiWorldSideRoomsDescription(roomsNormalized)
            : generateSingleWordSideDescription(wordSides[0] as IPlannerPropertyRoomExposureSide);

        return {
            hasManySides,
            wordSides,
            description,
            hasNorthSide: wordSides.includes("N") || wordSides.includes("NE") || wordSides.includes("NW"),
            hasEastSide: wordSides.includes("E") || wordSides.includes("NE") || wordSides.includes("SE"),
            hasWestSide: wordSides.includes("W") || wordSides.includes("NW") || wordSides.includes("SW"),
            hasSouthSide: wordSides.includes("S") || wordSides.includes("SE") || wordSides.includes("SW")
        };
    }, [props.propertyRooms]);

    if (!plannerPropertyRooms.hasNorthSide && !plannerPropertyRooms.hasEastSide && !plannerPropertyRooms.hasWestSide && !plannerPropertyRooms.hasSouthSide) {
        return null;
    }

    return (
        <PropertyLocationGeneralAttributeBox
            graphic={
                <div css={propertySidesWrapperStyle}>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasNorthSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Północ
                        </Text>
                    </div>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasEastSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Wschód
                        </Text>
                    </div>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasWestSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Zachód
                        </Text>
                    </div>
                    <div css={[propertySideStyle, plannerPropertyRooms.hasSouthSide && backgroundColor(theme.colors.primary)]}>
                        <Text as="span" variant="info_txt_3" css={propertySideTextStyle}>
                            Południe
                        </Text>
                    </div>
                </div>
            }
            title={plannerPropertyRooms.hasManySides ? "Mieszkanie dwustronne" : "Mieszkanie jednostronne"}
            description={plannerPropertyRooms.description}
        />
    );
};

const generateSingleWordSideDescription = (wordSide: IPlannerPropertyRoomExposureSide) => {
    return `Wszystkie okna w wybranym przez Ciebie mieszkaniu wychodzą na ${getPlannerPropertyWorldSideName(wordSide)}.`;
};

const propertySidesWrapperStyle = (theme: Theme) => css`
    overflow: hidden;
    display: grid;
    grid-template-columns: repeat(2, 2fr);
    background-color: ${theme.colors.secondary};
    border: 2px solid ${theme.colors.secondary};
    border-radius: 50%;
    transform: rotate(45deg);
    gap: 2px;
`;

const PROPERTY_BOX_SIZE_REM = 5;

const propertySideStyle = (theme: Theme) => css`
    width: ${PROPERTY_BOX_SIZE_REM}rem;
    height: ${PROPERTY_BOX_SIZE_REM}rem;
    position: relative;
    ${flexAbsoluteCenter};
    background-color: ${theme.colors.gray[200]};

    &:nth-of-type(1) > span,
    &:nth-of-type(4) > span {
        text-align: center;
        width: 100%;
        transform: rotate(-45deg);
    }

    &:nth-of-type(2) > span {
        left: 0;
        bottom: 0;
        transform-origin: center left;
        transform: translate(0, 50%) rotate(-45deg);
        text-align: right;
    }

    &:nth-of-type(3) > span {
        right: 0;
        top: 0;
        transform-origin: center right;
        transform: translate(0, -50%) rotate(-45deg);
    }
`;

const propertySideTextStyle = css`
    display: block;
    position: absolute;
    width: ${PROPERTY_BOX_SIZE_REM - 0.5}rem;
`;
